import axios, { AxiosError, AxiosRequestHeaders } from 'axios';
import { showAlert } from '../utilities/sweetalert';
import { logout } from '../utilities/authentication';
import { requestRefreshToken } from './authen';

const MAX_UNAUTHORIZED_RETRY = 1;

type RetryableConfig = {
  _unauthorizedRetryCount?: number;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_ECONSENT_URL,
  headers: {
    'Api-Key': process.env.REACT_APP_ECONSENT_API_KEY,
  },
});

// Request interceptor to add authorization header from localStorage
api.interceptors.request.use(
  (config) => {
    assignAuthorizationHeader(config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: on 401 try refresh token, then retry or logout
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const requestConfig = error.config as (AxiosError['config'] & RetryableConfig) | undefined;
    if (requestConfig && (requestConfig._unauthorizedRetryCount ?? 0) >= MAX_UNAUTHORIZED_RETRY) {
      return Promise.reject(error);
    }

    const refreshed = await handleUnauthorized(error);
    if (refreshed && requestConfig) {
      requestConfig._unauthorizedRetryCount = (requestConfig._unauthorizedRetryCount ?? 0) + 1;
      assignAuthorizationHeader(requestConfig.headers);
      return api.request(requestConfig);
    }
    return Promise.reject(error);
  }
);

function assignAuthorizationHeader(headers: AxiosRequestHeaders) {
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
}

async function handleUnauthorized(error: AxiosError): Promise<boolean> {
  if (!error.response || error.response.status !== 401) return false;

  const requestUrl = error.config?.url ?? '';
  // Exclude endpoints that should not trigger token refresh on 401
  const excludedPatterns = [
    /\/econsent\/dynamic-form\//,
    /\/dynamic-form\//,
    /\/transactions\/[^/]+\/candidates\/[^/]+\/verify/,
  ];
  if (excludedPatterns.some((pattern) => pattern.test(requestUrl))) return false;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    await showSessionExpiredAndLogout();
    return false;
  }

  const refreshed = await requestRefreshToken();
  if (refreshed) return true;
  
  await showSessionExpiredAndLogout();
  return false;
}

async function showSessionExpiredAndLogout() {
  await showAlert({
    icon: 'error',
    title: 'Session Expired',
    text: 'Your session has timed out. Please sign in again.',
    confirmButtonText: 'Sign In',
    allowOutsideClick: false,
  });
  logout();
}

export default api;